<template>
  <div class="shortcut-toggle" @click.stop="clickShortcut"></div>
  <div class="shortcut-header flex-start-end">
    <div class="shortcut-title block-title">自訂捷徑</div>
    <functionButtons
      v-if="isShortcutSortingAvailable"
      :hasSequence="!isSorting"
      :hasDone="isSorting"
      @onSequence="onSequence"
      @onDone="onDone"
    ></functionButtons>
  </div>
  <div class="shortcut-container">
    <div class="shortcut-blocks-container">
      <div
        v-for="(shortCut, index) in shortCuts"
        :key="shortCut.name"
        class="shortcut-block"
        :class="{
          sorting: isSorting,
          dragging: draggingShortcutType === shortCut.type,
        }"
        :data-shortcut-index="index"
        @pointerdown="startShortcutDrag(index, $event)"
        @click="clickShortCutItem(shortCut)"
      >
        <img
          v-if="getShortcutIcon(shortCut)"
          class="shortcut-icon"
          :src="getShortcutIcon(shortCut)"
          :alt="shortCut.shortcutGroupName"
        />
        <div>{{ shortCut.text }}</div>
      </div>
    </div>
    <div v-if="!isSorting" class="add-new-shortcut">
      <div class="add-button" @click="openSelectModal"></div>
    </div>
  </div>
  <shortCutModal
    :id="selectModalId"
    :title="selectModalTitle"
    :trigger="selectModalTrigger"
    :hasSearch="true"
    :items="items"
    :itemGroups="modalItemGroups"
    :selectedShortCuts="shortCuts"
    @changeGroup="changeSelectGroup"
    @closeModal="closeSelectModal"
    @saveModal="saveSelectModal"
  ></shortCutModal>
</template>

<script>
import shortCutModal from "@/components/modals/ShortCutModal.vue";
import functionButtons from "@/components/tools/FunctionButtons.vue";

export default {
  data() {
    return {
      shortCutToggle: false,
      clickHandler: null,
      timer: null,
      mouseenterHandler: null,
      mouseleaveHandler: null,
      countdown: 3,
      selectModalTrigger: false,
      selectModalId: "addShortCut",
      selectModalTitle: this.$t("modalTitle.addShortCut"),
      shortCuts: [],
      itemGroups: [],
      modalItemGroups: [],
      items: [],
      currentSearchType: 0,
      types: [],
      shortcutIcons: {
        Account: require("@/assets/images/Shortcut/Account.svg"),
        Basic: require("@/assets/images/Shortcut/Basic.svg"),
        Customer: require("@/assets/images/Shortcut/Customer.svg"),
        ERP: require("@/assets/images/Shortcut/ERP.svg"),
        OnlineOrder: require("@/assets/images/Shortcut/OnlineOrder.svg"),
        Report: require("@/assets/images/Shortcut/Report.svg"),
      },
      isSorting: false,
      isShortcutSortingAvailable: window.innerWidth >= 1200,
      draggingShortcutIndex: null,
      draggingShortcutType: null,
      draggingShortcutElement: null,
      shortcutDragImage: null,
      shortcutDragOffsetX: 0,
      shortcutDragOffsetY: 0,
      checkTimer: null,
    };
  },
  components: {
    shortCutModal,
    functionButtons,
  },
  watch: {
    shortCutToggle(value) {
      const shortCutContainer = document.getElementById("shortCut");
      if (value) {
        this.clickHandler = this.listeningClick.bind(this);
        this.mouseenterHandler = this.stopTimer.bind(this);
        this.mouseleaveHandler = this.setTimer.bind(this);
        document.addEventListener("click", this.clickHandler);
        shortCutContainer.addEventListener(
          "mouseenter",
          this.mouseenterHandler
        );
        shortCutContainer.addEventListener(
          "mouseleave",
          this.mouseleaveHandler
        );
      } else {
        document.removeEventListener("click", this.clickHandler);
        shortCutContainer.removeEventListener(
          "mouseenter",
          this.mouseenterHandler
        );
        shortCutContainer.removeEventListener(
          "mouseleave",
          this.mouseleaveHandler
        );
        this.clickHandler = null;
        this.mouseenterHandler = null;
        this.mouseleaveHandler = null;
      }
    },
  },
  emits: ["openShortcut"],
  methods: {
    onSequence() {
      if (!this.isShortcutSortingAvailable) return;
      this.isSorting = true;
    },
    onDone() {
      this.dropShortcut();
      this.saveSelectModal(this.shortCuts).then(() => {
        this.isSorting = false;
      });
    },
    startShortcutDrag(index, event) {
      if (!this.isSorting) return;
      if (event.button !== undefined && event.button !== 0) return;

      event.preventDefault();
      this.removeShortcutDragImage();

      const sourceElement = event.currentTarget;
      const dragImage = sourceElement.cloneNode(true);
      sourceElement.classList.add("shortcut-dragging-source");
      sourceElement.style.visibility = "hidden";

      this.draggingShortcutIndex = index;
      this.draggingShortcutType = this.shortCuts[index]?.type;
      this.draggingShortcutElement = sourceElement;

      dragImage.classList.add("shortcut-drag-image");
      dragImage.classList.remove("dragging");
      dragImage.classList.remove("shortcut-dragging-source");
      const rect = sourceElement.getBoundingClientRect();
      this.shortcutDragOffsetX = event.clientX - rect.left;
      this.shortcutDragOffsetY = event.clientY - rect.top;
      dragImage.style.width = `${rect.width}px`;
      dragImage.style.height = `${rect.height}px`;
      dragImage.querySelectorAll("img").forEach((img) => {
        img.src = img.currentSrc || img.src;
      });
      document.body.appendChild(dragImage);
      this.shortcutDragImage = dragImage;
      this.setShortcutDragImagePosition(event);

      window.addEventListener("pointermove", this.onShortcutPointerMove);
      window.addEventListener("pointerup", this.dropShortcut);
      window.addEventListener("pointercancel", this.dropShortcut);
    },
    onShortcutPointerMove(event) {
      if (!this.isSorting || this.draggingShortcutIndex === null) return;

      this.setShortcutDragImagePosition(event);

      const target = document
        .elementFromPoint(event.clientX, event.clientY)
        ?.closest(".shortcut-block");
      if (!target || target.classList.contains("dragging")) return;

      const targetIndex = Number(target.dataset.shortcutIndex);
      if (Number.isNaN(targetIndex)) return;

      this.moveDraggingShortcut(targetIndex);
    },
    setShortcutDragImagePosition(event) {
      if (!this.shortcutDragImage) return;

      this.shortcutDragImage.style.transform = `translate(${
        event.clientX - this.shortcutDragOffsetX
      }px, ${event.clientY - this.shortcutDragOffsetY}px)`;
    },
    moveDraggingShortcut(index) {
      if (!this.isSorting || this.draggingShortcutType === null) return;

      const draggingIndex = this.shortCuts.findIndex(
        (item) => item.type === this.draggingShortcutType
      );
      if (draggingIndex < 0 || draggingIndex === index) return;

      const draggingItem = this.shortCuts.splice(draggingIndex, 1)[0];
      this.shortCuts.splice(index, 0, draggingItem);
      this.draggingShortcutIndex = index;
    },
    dropShortcut() {
      if (this.draggingShortcutElement) {
        this.draggingShortcutElement.classList.remove(
          "shortcut-dragging-source"
        );
        this.draggingShortcutElement.style.visibility = "";
      }
      document.querySelectorAll(".shortcut-dragging-source").forEach((item) => {
        item.classList.remove("shortcut-dragging-source");
        item.style.visibility = "";
      });
      window.removeEventListener("pointermove", this.onShortcutPointerMove);
      window.removeEventListener("pointerup", this.dropShortcut);
      window.removeEventListener("pointercancel", this.dropShortcut);
      this.removeShortcutDragImage();
      this.draggingShortcutIndex = null;
      this.draggingShortcutType = null;
      this.draggingShortcutElement = null;
    },
    removeShortcutDragImage() {
      if (this.shortcutDragImage) {
        this.shortcutDragImage.remove();
        this.shortcutDragImage = null;
      }
    },
    clickShortCutItem(shortCut) {
      if (this.isSorting || !shortCut.name) return;
      if (this.$route.name === shortCut.name) return;

      this.$router.push({
        name: shortCut.name,
      });
    },
    changeSelectGroup(value) {
      const group = this.modalItemGroups.find((group) => group.value == value);
      this.items = this.getShortcutItems(group);
    },
    getShortcutItems(group) {
      return group?.categories?.filter((item) => item.type) || [];
    },
    getShortcutItem(item, groupName) {
      return {
        ...item,
        shortcutGroupName: groupName,
      };
    },
    getShortcutItemGroups(groups) {
      return groups
        .flatMap((group) => {
          if (group.name === "Report") {
            return (group.categories || [])
              .filter((category) => category.subcategories?.length)
              .map((category) => ({
                value: `${group.value}-${category.value}`,
                name: category.name,
                text: `${group.text} - ${category.text}`,
                categories: category.subcategories.map((item) =>
                  this.getShortcutItem(item, group.name)
                ),
              }));
          }

          return [
            {
              ...group,
              categories: (group.categories || []).map((item) =>
                this.getShortcutItem(item, group.name)
              ),
            },
          ];
        })
        .filter((group) => this.getShortcutItems(group).length);
    },
    saveSelectModal(items) {
      if (items.length > 9) {
        this.$func.message({
          type: "alert",
          mainMessage: "最多可設定9個捷徑",
        });
        return Promise.resolve();
      }

      return this.$api.shortcuts
        .post({ Types: items.map((item) => item.type) })
        .then(() => {
          this.getShortCuts();
          this.closeSelectModal();
        })
        .catch((error) => {
          this.$func.message({
            type: "error",
            mainMessage: error.data.Message,
          });
        });
    },
    closeSelectModal() {
      this.selectModalTrigger = false;
    },
    openSelectModal() {
      this.selectModalTrigger = true;
    },
    getShortcutIcon(shortCut) {
      return this.shortcutIcons[shortCut.shortcutGroupName] || "";
    },
    getShortCuts() {
      return this.$api.shortcuts
        .get()
        .then((data) => {
          this.types = data;
          const shortcutMap = new Map();

          this.modalItemGroups.forEach((group) => {
            group.categories?.forEach((item) => {
              shortcutMap.set(item.type, item);
            });
          });

          this.shortCuts = this.types
            .map((type) => shortcutMap.get(type))
            .filter((item) => item);
        })
        .catch((error) => {
          this.$func.message({
            type: "error",
            mainMessage: error.data.Message,
          });
        });
    },
    clickShortcut() {
      if (!this.shortCutToggle) {
        this.shortCutToggle = true;
        this.$emit("openShortcut", this.shortCutToggle);
      } else {
        this.shortCutToggle = false;
        this.$emit("openShortcut", this.shortCutToggle);
      }
    },
    listeningClick(e) {
      if (!e.target.closest("#shortCut") && this.shortCutToggle) {
        this.shortCutToggle = false;
        this.$emit("openShortcut", this.shortCutToggle);
      }
    },
    setTimer() {
      this.countdown = 3;
      if (this.timer) {
        clearInterval(this.timer);
      }
      this.timer = setInterval(() => {
        if (this.countdown > 0) {
          this.countdown--;
        } else {
          clearInterval(this.timer);
          this.shortCutToggle = false;
          this.$emit("openShortcut", this.shortCutToggle);
        }
      }, 1000);
    },
    stopTimer() {
      clearInterval(this.timer);
    },
    handleWindowResize() {
      const isAvailable = window.innerWidth >= 1200;
      if (!isAvailable && this.isSorting) {
        this.dropShortcut();
        this.isSorting = false;
        this.getShortCuts();
      }
      this.isShortcutSortingAvailable = isAvailable;
    },
  },
  beforeUnmount() {
    clearInterval(this.checkTimer);
    clearInterval(this.timer);
    document.removeEventListener("click", this.clickHandler);

    window.removeEventListener("resize", this.handleWindowResize);
    const shortCutContainer = document.getElementById("shortCut");
    if (shortCutContainer) {
      shortCutContainer.removeEventListener(
        "mouseenter",
        this.mouseenterHandler
      );
      shortCutContainer.removeEventListener(
        "mouseleave",
        this.mouseleaveHandler
      );
    }

    this.dropShortcut();
  },
  created() {
    this.checkTimer = setInterval(() => {
      this.authorities = JSON.parse(localStorage.getItem("authorities"));
      if (this.authorities) {
        clearInterval(this.checkTimer);
        this.itemGroups = this.$enum.navbarGroupType
          .getAll()
          .filter((type) => type.value != 6);
        this.modalItemGroups = this.getShortcutItemGroups(this.itemGroups);
        this.items = this.getShortcutItems(this.modalItemGroups[0]);
        this.getShortCuts();
      }
    }, 200); // 每200毫秒檢查一次
  },
  mounted() {
    window.addEventListener("resize", this.handleWindowResize);
  },
};
</script>

<style lang="scss">
.shortcut-title {
  @media screen and (max-width: 1199px) {
    display: none;
  }
}
.shortcut-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: white;
  overflow: hidden;
  border-radius: 12px;
  box-shadow: rgba(0, 0, 0, 0.16) 0px 1px 5px;
  @media screen and (max-width: 1199px) {
    box-shadow: rgba(0, 0, 0, 0.16) 0px -1px 5px;
    border-radius: 12px 12px 0 0;
  }
}
.shortcut-toggle {
  display: none;
  position: absolute;
  top: -20px;
  left: 50%;
  transform: translateX(-50%);
  width: 60px;
  height: 20px;
  border-radius: 8px 8px 0 0;
  background-color: white;
  box-shadow: 0 0 5px 1px rgba(0, 0, 0, 0.16); /* 整體陰影 */
  clip-path: inset(-5px -5px 0px -5px); /* 裁剪掉下方的陰影 */
  z-index: 20;
  @media screen and (max-width: 1199px) {
    display: block;
  }
}
.shortcut-blocks-container {
  display: flex;
  justify-content: flex-start;
  align-items: center;
  height: 112px;
  padding: 0 10px;
  overflow-x: auto;
}
.shortcut-block {
  cursor: pointer;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 6px;
  margin: 8px;
  padding: 8px;
  min-width: 80px;
  height: 80px;
  border: 1px solid #4695c9;
  border-radius: 8px;
  color: #4695c9;
  font-size: 14px;
  text-align: center;
  flex-shrink: 0;
  transition: all 0.3s;
  div {
    width: 100%;
    line-height: 1.25;
    word-break: break-word;
  }
  &:hover {
    background-color: #eef8ff;
  }
  &.sorting {
    cursor: grab;
    background-color: #fff !important;
  }
  &.dragging {
    cursor: grabbing !important;
    visibility: hidden !important;
    opacity: 0 !important;
    transition: none !important;
  }
}
.shortcut-dragging-source {
  visibility: hidden !important;
  opacity: 0 !important;
  transition: none !important;
}
.shortcut-drag-image {
  position: fixed;
  top: 0;
  left: 0;
  z-index: 9999;
  margin: 0;
  pointer-events: none;
  background-color: #fff;
  box-shadow: rgba(0, 0, 0, 0.16) 0 2px 8px;
  transition: none;
}
.shortcut-icon {
  width: 28px;
  height: 28px;
  object-fit: contain;
  flex-shrink: 0;
}
.add-new-shortcut {
  width: 80px;
  height: 112px;
  display: flex;
  justify-content: center;
  align-items: center;
  box-shadow: rgba(0, 0, 0, 0.16) -1px 0px 3px;
}
.add-button {
  cursor: pointer;
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 40px;
  height: 40px;
  color: white;
  background-color: #4695c9;
  border-radius: 50%;
  font-size: 24px;
  font-weight: 300;
  line-height: 50px;
}
.add-button::before {
  content: "＋";
  position: absolute;
  top: 52%;
  left: 49%;
  transform: translate(-50%, -50%);
}
</style>
